import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { IJsonModel } from 'flexlayout-react';
import { DEFAULT_LAYOUT, getDefaultLayout, ensureDockBorders } from '../layouts/defaultLayout';
import type { SidebarPosition } from './uiStore';

// v7: remove the retired Overview + Hub panels from saved layouts (pruning any
// tabsets left empty), so they don't linger as "Unknown panel" placeholders.
// v6: add dock borders on all four edges (top/left/right/bottom) so ANY panel
// can be side-docked, not just Projects — injected into existing saved layouts
// without discarding the user's arrangement.
// v5: refresh the `global` block on persisted layouts so tab drag/drop
// (tabEnableDrag / tabSetEnableDrop / tabSetEnableDivide / enableEdgeDock)
// is enabled for users whose saved layout predates those flags.
const LAYOUT_VERSION = 7;

/** Retired panel components removed in v7. */
const REMOVED_COMPONENTS = new Set(['overview', 'hub']);

/**
 * Remove tabs whose `component` is retired, recursively, and prune tabsets/rows
 * left empty as a result. Borders are kept even when empty (they're dock strips).
 */
function stripRemovedTabs(model: IJsonModel): IJsonModel {
  const clone = JSON.parse(JSON.stringify(model)) as IJsonModel;
  type Node = { type?: string; component?: string; children?: Node[] };
  const walk = (children: Node[] = []): Node[] =>
    children
      .filter((c) => !(c.type === 'tab' && c.component && REMOVED_COMPONENTS.has(c.component)))
      .map((c) => (Array.isArray(c.children) ? { ...c, children: walk(c.children) } : c))
      .filter(
        (c) =>
          !(
            (c.type === 'tabset' || c.type === 'row') &&
            Array.isArray(c.children) &&
            c.children.length === 0
          ),
      );
  const root = clone.layout as unknown as Node;
  if (Array.isArray(root?.children)) root.children = walk(root.children);
  if (Array.isArray(clone.borders)) {
    clone.borders = clone.borders.map((b) => ({
      ...b,
      children: walk((b as unknown as Node).children) as never,
    })) as IJsonModel['borders'];
  }
  return clone;
}

interface LayoutState {
  layoutJson: IJsonModel;
  layoutVersion: number;
  resetCounter: number;
  setLayoutJson: (json: IJsonModel) => void;
  resetLayout: (position?: SidebarPosition) => void;
  setBorderPosition: (position: SidebarPosition) => void;
}

export const useLayoutStore = create<LayoutState>()(
  persist(
    (set, get) => ({
      layoutJson: DEFAULT_LAYOUT,
      layoutVersion: LAYOUT_VERSION,
      resetCounter: 0,
      setLayoutJson: (json) => set({ layoutJson: json }),
      resetLayout: (position?: SidebarPosition) =>
        set({
          layoutJson: getDefaultLayout(position || 'left'),
          layoutVersion: LAYOUT_VERSION,
          resetCounter: get().resetCounter + 1,
        }),
      setBorderPosition: (position: SidebarPosition) => {
        const current = get().layoutJson;
        // Deep clone to avoid mutating the stored object
        const patched = JSON.parse(JSON.stringify(current)) as IJsonModel;
        type B = { location: string; children?: { component?: string }[] };
        const borders = (patched.borders ?? []) as unknown as B[];
        // Move ONLY the border that holds the Projects tab to the chosen side
        // (now that all four edges have borders, the old "move every border"
        // logic would stack them all on one edge).
        const fromIdx = borders.findIndex((b) =>
          (b.children ?? []).some((c) => c.component === 'projects'),
        );
        if (fromIdx >= 0) {
          const projTab = (borders[fromIdx].children ?? []).find(
            (c) => c.component === 'projects',
          );
          borders[fromIdx].children = (borders[fromIdx].children ?? []).filter(
            (c) => c.component !== 'projects',
          );
          let target = borders.find((b) => b.location === position);
          if (!target) {
            target = { location: position, children: [] };
            borders.push(target as B);
          }
          if (projTab) (target.children ??= []).push(projTab);
          patched.borders = ensureDockBorders(borders as never) as IJsonModel['borders'];
        }
        set({ layoutJson: patched, resetCounter: get().resetCounter + 1 });
      },
    }),
    {
      name: 'ateliercode-layout',
      version: LAYOUT_VERSION,
      partialize: (state) => ({
        layoutJson: state.layoutJson,
        layoutVersion: state.layoutVersion,
      }),
      migrate: (_persistedState: unknown, version: number) => {
        // Force reset to new default when migrating from v3 or earlier
        // (v4 replaced chat tab with terminal tab)
        if (version < 4) {
          return {
            layoutJson: DEFAULT_LAYOUT,
            layoutVersion: LAYOUT_VERSION,
            resetCounter: 0,
            setLayoutJson: () => {},
            resetLayout: () => {},
            setBorderPosition: () => {},
          };
        }
        const state = _persistedState as Partial<LayoutState> | undefined;
        // Preserve the user's panel arrangement but refresh the `global`
        // block with the current drag/drop-enabled defaults. Older saved
        // layouts serialized a global block that lacked the tab-drag flags,
        // which prevented dragging tabs between areas to regroup them.
        const persisted = state?.layoutJson;
        const merged: IJsonModel = persisted
          ? {
              ...persisted,
              global: {
                ...DEFAULT_LAYOUT.global,
                ...persisted.global,
                // Force-enable drag/drop regardless of what was saved.
                tabEnableDrag: true,
                tabSetEnableDrop: true,
                tabSetEnableDrag: true,
                tabSetEnableDivide: true,
                enableEdgeDock: true,
                // Keep empty dock strips visible on every edge (v6).
                borderEnableAutoHide: false,
              },
              // v6: ensure all four dock edges exist as drop targets, keeping
              // whatever the user already has docked.
              borders: ensureDockBorders(
                (persisted.borders ?? []) as never,
              ) as IJsonModel['borders'],
            }
          : DEFAULT_LAYOUT;
        // v7: strip retired Overview/Hub tabs (+ prune empty tabsets).
        const cleaned = stripRemovedTabs(merged);
        return {
          layoutJson: cleaned,
          layoutVersion: LAYOUT_VERSION,
          resetCounter: 0,
          setLayoutJson: () => {},
          resetLayout: () => {},
          setBorderPosition: () => {},
        };
      },
    }
  )
);
